import React, { FC, PropsWithChildren, ReactNode } from 'react'

import { styled } from 'styled-components'

import { PendingEventHandler } from 'core/components/lib/Button/Button'
import Clickable from 'core/components/lib/Clickable'
import ConfirmDialog from 'core/components/lib/ConfirmDialog'
import { R } from 'core/helpers'

import CardSection from './CardSection'

type BackButtonProps = PropsWithChildren<{
  color?: string
  onClick?: () => void
  url?: string
  to?: string
}>

export const BackButton: FC<BackButtonProps> = ({
  children = 'Back',
  onClick,
  url,
  to,
}) => {
  if (!onClick && !url && !to) return null

  return (
    <Clickable
      variant='ghost'
      {...(onClick ? { onClick } : to ? { to } : { href: url })}
    >
      {children}
    </Clickable>
  )
}

type CardSectionSubmitProps = {
  submitText?: string
  cancelText?: string
  margin?: string
  onCancel?: () => void
  onSubmit?: PendingEventHandler<
    React.MouseEvent<HTMLButtonElement, MouseEvent>
  >
  children?: ReactNode
  disabled?: boolean
  loading?: boolean
  success?: boolean
  skipConfirm?: boolean
}

const CardSectionSubmit: FC<CardSectionSubmitProps> = ({
  submitText = 'Submit',
  cancelText,
  margin,
  onCancel,
  onSubmit = R.noop,
  children,
  disabled = false,
  loading = false,
  success = false,
  skipConfirm,
}) => {
  const common = {
    children: loading ? 'Processing' : submitText,
    disabled: disabled || loading,
    variant: 'primary' as const,
  }

  return (
    <CardSection margin={margin}>
      <SubmitButtonsContainer>
        {!success && <BackButton onClick={onCancel}>{cancelText}</BackButton>}

        {children ??
          (success ? (
            <BackButton>Done</BackButton>
          ) : skipConfirm ? (
            <Clickable type='submit' {...common} />
          ) : (
            <ConfirmDialog
              content={`Are you sure you want to ${submitText.toLowerCase()}?`}
              onConfirm={onSubmit}
              title={submitText}
            >
              <Clickable {...common} />
            </ConfirmDialog>
          ))}
      </SubmitButtonsContainer>
    </CardSection>
  )
}

export default CardSectionSubmit

export const SubmitButtonsContainer = styled.div<{ margin?: string }>`
  display: flex;
  flex-direction: row;
  gap: 24px;
  align-items: center;
  justify-content: flex-end;
`
